import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '@/api/axios';
import { Loader2, Trash2, Upload } from 'lucide-react';

const SECCIONES = [
  { key: 'banners', titulo: '🖼️ Banners de encabezado', hint: 'Rotan solos al inicio de la página Soluciones. La imagen se muestra COMPLETA ajustada al área. Máx 6.', max: 6 },
  { key: 'ofertas', titulo: '🏷️ Ofertas y Descuentos', hint: 'Sube tus artes de ofertas/descuentos. Aparecen en la sección "Ofertas" de la página. Puedes programar vigencia: al vencer, la oferta se quita sola. Fechas vacías = siempre activa. Máx 12.', max: 12 },
];

const ItemCard = ({ seccion, item, onDelete }) => {
  const [titulo, setTitulo] = useState(item.titulo || '');
  const [desde, setDesde] = useState(item.desde || '');
  const [hasta, setHasta] = useState(item.hasta || '');
  const hoy = new Date().toISOString().slice(0, 10);

  const guardar = async (campos) => {
    try {
      await api.patch(`/landing/admin/soluciones/portada/${seccion}/${item.id}`, { titulo, desde, hasta, ...campos });
      toast.success('Guardado');
    } catch (err) { toast.error(err?.response?.data?.detail || 'No se pudo guardar'); }
  };

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden" data-testid={`sol-${seccion}-item-${item.id}`}>
      <div className="relative">
        <img src={item.url} alt="" className="w-full h-28 object-cover" />
        <button onClick={onDelete} data-testid={`sol-${seccion}-del-${item.id}`}
          className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-red-600 text-white p-1.5 rounded transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        {seccion === 'ofertas' && hasta && hasta < hoy && <span className="absolute bottom-1.5 left-1.5 bg-red-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded" data-testid={`sol-oferta-vencida-${item.id}`}>VENCIDA</span>}
        {seccion === 'ofertas' && desde && desde > hoy && <span className="absolute bottom-1.5 left-1.5 bg-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded" data-testid={`sol-oferta-programada-${item.id}`}>PROGRAMADA</span>}
      </div>
      <input value={titulo} onChange={(e) => setTitulo(e.target.value)} onBlur={() => titulo !== (item.titulo || '') && guardar({ titulo })}
        placeholder="Título (opcional)" data-testid={`sol-${seccion}-titulo-${item.id}`}
        className="w-full bg-transparent border-t border-slate-200 dark:border-slate-700 px-2 py-1.5 text-[11px] outline-none" />
      {seccion === 'ofertas' && (
        <div className="flex items-center gap-1 px-2 py-1.5 border-t border-slate-200 dark:border-slate-700 text-[10px] flex-wrap">
          <span className="opacity-60 font-bold">Vigencia:</span>
          <input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); guardar({ desde: e.target.value }); }}
            data-testid={`sol-oferta-desde-${item.id}`} title="Fecha de inicio (opcional)"
            className="bg-transparent border border-slate-300 dark:border-slate-600 rounded px-1 py-0.5 text-[10px] outline-none" />
          <span className="opacity-50">a</span>
          <input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); guardar({ hasta: e.target.value }); }}
            data-testid={`sol-oferta-hasta-${item.id}`} title="Fecha de fin (opcional). Al vencer, la oferta se quita sola de la página."
            className="bg-transparent border border-slate-300 dark:border-slate-600 rounded px-1 py-0.5 text-[10px] outline-none" />
        </div>
      )}
    </div>
  );
};

export const SolucionesPortadaManager = () => {
  const [data, setData] = useState(null);
  const [subiendo, setSubiendo] = useState('');

  const cargar = () => api.get('/landing/admin/soluciones/portada').then(({ data }) => setData(data)).catch(() => toast.error('No se pudo cargar la portada de Soluciones'));
  useEffect(() => { cargar(); }, []);

  const subir = async (seccion, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('titulo', '');
    try {
      setSubiendo(seccion);
      await api.post(`/landing/admin/soluciones/portada/${seccion}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Imagen subida. Ya se ve en la página Soluciones.');
      cargar();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error subiendo imagen'); }
    finally { setSubiendo(''); e.target.value = ''; }
  };

  const eliminar = async (seccion, id) => {
    try {
      await api.delete(`/landing/admin/soluciones/portada/${seccion}/${id}`);
      toast.success('Imagen eliminada');
      cargar();
    } catch { toast.error('No se pudo eliminar'); }
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-400" /></div>;

  return (
    <div className="space-y-4" data-testid="soluciones-portada-manager">
      <div>
        <h2 className="text-lg font-bold">🏠 Portada de la Página Soluciones</h2>
        <p className="text-xs text-[var(--text-muted)]">Personaliza los banners del encabezado y la sección de ofertas de /soluciones. Los cambios se ven al instante.</p>
      </div>
      {SECCIONES.map((s) => (
        <div key={s.key} className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <p className="font-black text-sm">{s.titulo}</p>
          <p className="text-xs opacity-60 mb-3">{s.hint}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-2">
            {(data[s.key] || []).map((it) => (
              <ItemCard key={it.id} seccion={s.key} item={it} onDelete={() => eliminar(s.key, it.id)} />
            ))}
          </div>
          {(data[s.key] || []).length < s.max && (
            <label data-testid={`sol-${s.key}-upload-btn`} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border border-blue-500/50 text-blue-500 hover:bg-blue-500/10 cursor-pointer transition-colors">
              {subiendo === s.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {subiendo === s.key ? 'Subiendo…' : 'Subir imagen'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => subir(s.key, e)} data-testid={`sol-${s.key}-upload`} />
            </label>
          )}
        </div>
      ))}
    </div>
  );
};
